import { JSX, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Identifier, useGetIdentity, UseGetIdentityResult, useNotify, useRefresh } from 'react-admin';
import { AdminMiniDescriptor, AdminMiniSendReport } from '../admin-mini.types';
import { useAdminMiniAccess, AdminMiniAccess } from '../hooks/admin-mini-access';
import { useAdminMiniCancel } from '../hooks/admin-mini-requests';
import { AdminMiniRunList } from './AdminMiniRunList';
import { AdminMiniResultsPanel } from './AdminMiniResultsPanel';
import { AdminMiniResultModal } from './AdminMiniResultModal';

export interface AdminMiniWorkspaceProps {
    tool: AdminMiniDescriptor;
    /** A short line under the heading saying what this screen is. */
    blurb: string;
    /** The button that opens this tool's composer. Rendered only with the run permission. */
    createControl: (access: AdminMiniAccess) => JSX.Element;
}

/**
 * One admin Mini tool's screen: the runs, their results, and one result opened.
 *
 * Both tools render this. Everything they differ about is in the descriptor, and the composer is
 * passed in because that is the one part with a payload of its own.
 *
 * The shape is the portal's: **the list is the navigation and the result is the page.** Picking a
 * run fills the panel beside it; picking a row in that panel opens the result, which leads with how
 * it scored and then shows what was said.
 */
export const AdminMiniWorkspace: (props: AdminMiniWorkspaceProps) => JSX.Element =
    (props: AdminMiniWorkspaceProps): JSX.Element => {
        const { tool } = props;
        const user: UseGetIdentityResult = useGetIdentity();
        const notify = useNotify();
        const refresh = useRefresh();
        const access = useAdminMiniAccess(tool);
        const { cancelling, cancel } = useAdminMiniCancel(tool);

        const [openRun, setOpenRun] = useState<{ id: Identifier, name: string } | undefined>(undefined);
        const [openResult, setOpenResult] = useState<any>(undefined);

        async function handleCancel(id: Identifier): Promise<void> {
            const answer: AdminMiniSendReport = await cancel({ id: id });
            notify(answer.message, { type: answer.status === 'success' ? 'info' : 'warning' });
            if (answer.status === 'success') {
                refresh();
            }
        }

        // Permissions are still in flight. Refusing now would flash an access denied at an admin
        // who is perfectly entitled to be here.
        if (access.loading) {
            return <></>;
        }

        if (!access.can('open')) {
            return (
                <Box sx={ { padding: '48px', textAlign: 'center', color: 'text.secondary' } }>
                    <Typography sx={ { fontSize: '15px' } }>
                        { `Your role does not include the ${ tool.title }.` }
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={ { width: '100%' } }>
                <Box sx={ {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    paddingBottom: '14px',
                    marginBottom: '18px',
                } }>
                    <Box>
                        <Typography component="h3" sx={ { fontSize: '16px', fontWeight: 700 } }>
                            { `Your ${ tool.runNoun }s` }
                        </Typography>
                        <Typography sx={ { fontSize: '13px', color: 'text.secondary' } }>
                            { props.blurb }
                        </Typography>
                    </Box>

                    { access.can('run') && props.createControl(access) }
                </Box>

                <Box sx={ {
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
                    gap: '20px',
                    alignItems: 'start',
                } }>
                    <AdminMiniRunList tool={ tool }
                                      adminUserId={ user?.data?.id }
                                      selectedId={ openRun?.id }
                                      cancelling={ cancelling }
                                      access={ access }
                                      onShowResults={ (run: any): void =>
                                          setOpenRun({ id: run.id, name: run.name }) }
                                      onCancel={ (id: Identifier): void => { void handleCancel(id); } } />

                    <AdminMiniResultsPanel tool={ tool }
                                           run={ openRun }
                                           onOpenResult={ setOpenResult } />
                </Box>

                <AdminMiniResultModal tool={ tool }
                                      open={ openResult !== undefined }
                                      onClose={ (): void => setOpenResult(undefined) }
                                      result={ openResult }
                                      runId={ openRun?.id }
                                      access={ access } />
            </Box>
        );
    };
